import { ApolloQueryResult, useMutation } from '@apollo/client';
import { AddCircle as AddCircleIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import {
  Badge,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import { keyBy, merge, values } from 'lodash';
import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from 'react';
import * as WebTabs from 'react-web-tabs';
import semver from 'semver';
import { makeStyles } from 'tss-react/mui';

import ConfirmDialog from '@/components/dialogs/confirm-dialog';
import { deviceUpdateDevice, deviceUpdateNetworkSettings, forgetWiFi } from '@/graphql/mutations';
import { useTranslation, useURLQuery } from '@/hooks';

import { SyncBadge } from './device-config-card';
import EditWiFiDialog from './edit-wifi-dialog';

const useStyles = makeStyles()((theme) => ({
  tab: {
    background: 'transparent',
    border: 0,
    fontFamily: theme.typography.fontFamily,
    display: 'inline-block',
    textAlign: 'left',
    fontSize: 'inherit',
    padding: '0.8rem 2rem',
    margin: '0.2rem 0',
    transition: 'background 0.3s cubic-bezier(0.22, 0.61, 0.36, 1)',
    [theme.breakpoints.down('sm')]: {
      padding: '0.8rem',
    },
    '&:focus': {
      outline: 0,
      backgroundColor: '#f4f4f4',
    },
    '&[aria-selected="false"]:hover': {
      outline: 0,
      backgroundColor: '#f4f4f4',
    },
    '&[aria-selected="true"]': {
      position: 'relative',
      '&:after': {
        content: '""',
        position: 'absolute',
        right: '-1px',
        top: 0,
        height: '100%',
        borderRight: `3px solid ${theme.palette.primary.main}`,
      },
    },
  },
  invalidTab: {
    borderLeft: '2px',
    borderColor: 'red',

    borderStyle: 'solid',
  },
  tabPanel: {
    padding: '0px 20px',
    height: '100%',
    width: '100%',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(),
  },
  iconMargin: {
    marginRight: theme.spacing(),
  },
  invalidatedNetwork: {
    backgroundColor: theme.palette.grey[300],
  },
}));

interface TabProperties {
  network?: Pick<Schema.NetworkConfig, 'general' | 'wifi'>;
}

export const Tab: FunctionComponent<TabProperties> = (props) => {
  const [t] = useTranslation(['shared']);
  const { classes, cx } = useStyles();

  // No wifi settings available, so don't show the wifi config tab
  if (!props.network?.wifi) {
    return null;
  }

  const isValid = true;

  return (
    <WebTabs.Tab className={isValid ? classes.tab : cx(classes.tab, classes.invalidTab)} tabFor="wifi">
      <Badge badgeContent={<SyncBadge pendingChanges={!!props.network?.wifi?.desired} />}>
        <Typography variant="button">{t(['device:wifi'], { defaultValue: 'WiFi' })}</Typography>
      </Badge>
    </WebTabs.Tab>
  );
};

interface Properties extends TabProperties {
  applicationVersion: semver.SemVer | null;
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<Schema.AdministrationDevicePeripheralsQuery>>;
}

const isUsingFallback = ({ fallback }: Schema.GeneralNetworkSettings = {}): boolean | null => {
  switch (fallback) {
    case undefined:
    case null:
      return null;
    case Schema.NetworkFallbackStrategy.FALLBACK_TO_CELLULAR:
      return true;
    default:
      console.error('Failed to determine fallback strategy');
    case Schema.NetworkFallbackStrategy.NEVER:
      return false;
  }
};

export const TabPanel: FunctionComponent<Properties> = ({ network, ...props }) => {
  const { classes, cx } = useStyles();
  const [t] = useTranslation();
  const { softwareId = '' } = useURLQuery();

  const reportedWifi = network?.wifi?.reported;
  const desiredWifi = network?.wifi?.desired;

  const desiredNetworks = desiredWifi?.networks || [];
  const networks = values(merge(keyBy(reportedWifi?.networks || [], 'id'), keyBy(desiredNetworks, 'id')));

  const [wifiEnabled, setWifiEnabled] = useState(desiredWifi?.enabled || reportedWifi?.enabled || false);

  useEffect(() => {
    setWifiEnabled(desiredWifi?.enabled || reportedWifi?.enabled || false);
  }, [desiredWifi?.enabled, reportedWifi?.enabled]);

  // We set the toggle on by default if it has been enabled
  const [fallbackToggeled, setFallbackToggled] = useState(isUsingFallback(network?.general.desired) ?? false);

  const fallbackSupport = useMemo((): 'Too old' | 'Wifi disabled' | 'Supported' => {
    if (props.applicationVersion === null || semver.lt(props.applicationVersion, '3.5.0')) {
      return 'Too old';
    }
    if (wifiEnabled === false) {
      return 'Wifi disabled';
    }
    return 'Supported';
  }, [props.applicationVersion, network, wifiEnabled]);

  const [editting, setEditDialog] = useState<{ open: boolean; networkId: string | null }>({
    open: false,
    networkId: null,
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState({});

  const [updateDevice, { loading: deviceLoading }] = useMutation<
    Schema.DeviceUpdateDeviceMutation,
    Schema.DeviceUpdateDeviceMutationVariables
  >(deviceUpdateDevice);

  const [forgetWiFiMut, { loading: noWifiLoading }] = useMutation<
    Schema.ForgetWiFiMutation,
    Schema.ForgetWiFiMutationVariables
  >(forgetWiFi);

  const [updateNetworkSettings, { loading: networkSettingLoading }] = useMutation<
    Schema.DeviceUpdateNetworkSettingsMutation,
    Schema.DeviceUpdateNetworkSettingsMutationVariables
  >(deviceUpdateNetworkSettings);

  const loading = props.loading || deviceLoading || noWifiLoading || networkSettingLoading;

  // No wifi settings available, so don't show the wifi config tab
  if (!network?.wifi) {
    return null;
  }

  const onClickEdit = (value: string | null) => () => {
    setEditDialog({ open: true, networkId: value });
  };

  const onClickForget = (value: string) => () => {
    setConfirmDialogProps({
      onConfirm: () => onConfirmForget(value),
      title: t(['device:forgetWiFi'], { defaultValue: 'Forget network' }),
      content: t(['device:confirmForgetWiFi'], {
        defaultValue: 'Are you sure you want to forget "{{ networkName }}?"',
        networkName: networks.find((n) => n.id === value)?.ssid || 'Unknown',
      }),
    });
    setShowConfirmDialog(true);
  };

  const onConfirmForget = async (value: string) => {
    await forgetWiFiMut({
      variables: {
        uuid: softwareId,
        input: value,
      },
    });
    await refetchData();
    setShowConfirmDialog(false);
  };

  const handleWifiEnabled = async (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setWifiEnabled(checked);
    await updateDevice({
      variables: {
        uuid: softwareId,
        meta: {
          wifiEnabled: checked,
        },
      },
    });
    await refetchData();
  };

  const toggleFallback = async (_: unknown, checked: boolean) => {
    setFallbackToggled(checked);
    await updateNetworkSettings({
      variables: {
        uuid: softwareId,
        input: {
          fallback: checked
            ? Schema.NetworkFallbackStrategy.FALLBACK_TO_CELLULAR
            : Schema.NetworkFallbackStrategy.NEVER,
        },
      },
    });
    await refetchData();
  };

  const refetchData = async () => {
    await props.refetch();
    setTimeout(() => props.refetch(), 5000);
  };

  return (
    <WebTabs.TabPanel className={classes.tabPanel} tabId="wifi">
      <div className={classes.headerContainer}>
        <Grid alignItems="center" display="flex">
          <FormControlLabel
            control={<Switch disabled={loading} checked={wifiEnabled} onChange={handleWifiEnabled} color="primary" />}
            label={t(['device:enableWifi'], { defaultValue: 'Enable WiFi' })}
          />
          {fallbackSupport === 'Supported' && (
            <>
              <FormControlLabel
                control={
                  <Switch disabled={loading} checked={fallbackToggeled} onChange={toggleFallback} color="primary" />
                }
                label={t('device:enableFallback', { defaultValue: 'Fallback to cellular' })}
              />
              <Tooltip
                title={t('device:enableFallbackTooltip', {
                  defaultValue: `When enabled, the Factbird Duo will automatically use the cellular connection if internet over WiFi stops working.\n\n When disabled, the cellular module will be turned off and only the WiFi module will be able to access the internet.`,
                })}
              >
                <InfoIcon />
              </Tooltip>
            </>
          )}
        </Grid>

        <Tooltip title={t(['device:addConnectionTooltip'], { defaultValue: 'Add a new WiFi connection' })}>
          <Button color="primary" aria-label="add" onClick={onClickEdit(null)}>
            <AddCircleIcon className={classes.iconMargin} />
            {t(['device:addConnection'], { defaultValue: 'Add Connection' })}
          </Button>
        </Tooltip>
      </div>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('shared:wifiName', { defaultValue: 'Wifi name (SSID)' })}</TableCell>
              <TableCell align="right">{t(['shared:security'], { defaultValue: 'Security' })}</TableCell>
              <TableCell align="right">{t(['shared:actions'], { defaultValue: 'Actions' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {networks.map((network) => {
              const invalidatedNetwork = !!desiredWifi && !desiredNetworks.some((n) => n.id === network.id);

              return (
                <TableRow key={network.id} className={cx({ [classes.invalidatedNetwork]: invalidatedNetwork })}>
                  <TableCell component="th" scope="row">
                    {network.ssid}
                  </TableCell>
                  <TableCell align="right">{network.password ? 'WPA2' : 'None'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t(['shared:edit'], { defaultValue: 'Edit' })}>
                      <IconButton
                        aria-label="edit"
                        size="small"
                        onClick={onClickEdit(network.id)}
                        disabled={invalidatedNetwork}
                        className={classes.iconMargin}
                      >
                        <VisibilityIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t(['shared:forget'], { defaultValue: 'Forget' })}>
                      <IconButton
                        disabled={invalidatedNetwork}
                        edge="end"
                        aria-label="forget"
                        size="small"
                        onClick={onClickForget(network.id)}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} {...confirmDialogProps} />

      {editting.open ? (
        <EditWiFiDialog
          refetch={refetchData}
          open={editting.open}
          handleOpenClose={() => setEditDialog({ open: false, networkId: null })}
          initialState={networks.find((n) => n.id === editting.networkId) || null}
        />
      ) : null}
    </WebTabs.TabPanel>
  );
};
